import { CAPTURED_STYLES, TARGET_LIMITS, type NoteTarget } from '@spotnote/shared/target'

/**
 * Resolves an annotation to the element(s) underneath it.
 *
 * This is what lets a note say "the <h1> reading 'Seasonal plates'" instead of
 * "the point 350,320" — the difference between an agent grepping the repo in
 * one step and guessing from a screenshot.
 */

const WIDGET_HOST_ID = 'spotnote-root'

/** Attributes worth keeping: each one is something you can search a repo for. */
const USEFUL_ATTRS = ['role', 'aria-label', 'alt', 'href', 'name', 'type', 'placeholder', 'title']

/** Ids and testids that look generated carry no meaning for a human or a grep. */
const GENERATED = /^(?:radix|headlessui|mui|react-aria|:r[0-9a-z]+:|[0-9a-f-]{16,})/i

function isUsableIdent(value: string): boolean {
  return /^[A-Za-z][\w-]*$/.test(value) && !GENERATED.test(value)
}

function collapse(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

/** `CSS.escape` is not universally present (and absent under jsdom). */
function escapeIdent(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value)
  return value.replace(/([^\w-])/g, '\\$1')
}

function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`
}

/**
 * A selector that identifies the element within its document.
 *
 * Prefers a stable hook (id, test id) and otherwise walks up building the
 * shortest structural path that is unique. Deliberately does *not* lean on
 * class names: utility and CSS-module classes are noise or hashes, and the raw
 * list is stored separately for anyone who wants it.
 */
export function cssPath(el: Element, root: Document | ShadowRoot = el.ownerDocument!): string {
  const id = el.getAttribute('id')
  if (id && isUsableIdent(id) && root.querySelectorAll(`#${escapeIdent(id)}`).length === 1) {
    return `#${id}`
  }

  for (const attr of ['data-testid', 'data-test-id', 'data-test', 'data-cy']) {
    const value = el.getAttribute(attr)
    if (value) {
      const candidate = `[${attr}="${value}"]`
      try {
        if (root.querySelectorAll(candidate).length === 1) return candidate
      } catch {
        // An unescapable value; fall through to the structural path.
      }
    }
  }

  const segments: string[] = []
  let current: Element | null = el

  while (current && current.nodeType === 1) {
    const tag = current.tagName.toLowerCase()
    if (tag === 'html') break

    const parent: Element | null = current.parentElement
    let segment = tag

    if (parent) {
      const sameTag = Array.from(parent.children).filter(
        (child) => child.tagName === current!.tagName,
      )
      if (sameTag.length > 1) {
        segment += `:nth-of-type(${sameTag.indexOf(current) + 1})`
      }
    }

    segments.unshift(segment)

    // Stop as soon as the path is unambiguous — a short selector is easier to
    // read and less likely to break than a full root-to-leaf chain.
    const candidate = segments.join(' > ')
    try {
      if (root.querySelectorAll(candidate).length === 1) return truncate(candidate, TARGET_LIMITS.maxSelector)
    } catch {
      // Ignore and keep walking.
    }

    if (tag === 'body') break
    current = parent
  }

  return truncate(segments.join(' > ') || el.tagName.toLowerCase(), TARGET_LIMITS.maxSelector)
}

function attrsOf(el: Element): Record<string, string> {
  const attrs: Record<string, string> = {}

  for (const attr of Array.from(el.attributes)) {
    const keep = attr.name.startsWith('data-') || USEFUL_ATTRS.includes(attr.name)
    if (!keep || !attr.value) continue

    attrs[attr.name] = truncate(attr.value, TARGET_LIMITS.maxAttrLength)
    if (Object.keys(attrs).length >= TARGET_LIMITS.maxAttrs) break
  }

  return attrs
}

function stylesOf(el: Element): Record<string, string> {
  const styles: Record<string, string> = {}
  if (typeof getComputedStyle !== 'function') return styles

  const computed = getComputedStyle(el)
  for (const property of CAPTURED_STYLES) {
    const value = computed[property as keyof CSSStyleDeclaration]
    if (typeof value === 'string' && value) styles[property] = truncate(value, 120)
  }

  return styles
}

function textOf(el: Element): string | null {
  // innerText reflects what is actually rendered — it skips hidden nodes and
  // respects line breaking — which is closer to what a person described.
  const raw = (el as HTMLElement).innerText ?? el.textContent ?? ''
  const text = collapse(raw)
  return text ? truncate(text, TARGET_LIMITS.maxText) : null
}

/** Describes one element. Split out from hit-testing so it can be tested. */
export function describeElement(el: Element, scrollX = 0, scrollY = 0): NoteTarget {
  const box = el.getBoundingClientRect()

  return {
    selector: cssPath(el),
    tag: el.tagName.toLowerCase(),
    id: el.getAttribute('id') || null,
    classes: Array.from(el.classList)
      .slice(0, TARGET_LIMITS.maxClasses)
      .map((name) => truncate(name, TARGET_LIMITS.maxClassLength)),
    attrs: attrsOf(el),
    text: textOf(el),
    rect: {
      x: Math.round(box.left + scrollX),
      y: Math.round(box.top + scrollY),
      w: Math.round(box.width),
      h: Math.round(box.height),
    },
    styles: stylesOf(el),
  }
}

/**
 * The topmost page element at a viewport point, ignoring the widget's own UI.
 *
 * `elementsFromPoint` is used rather than the singular form because the widget
 * overlay covers the viewport while a tool is armed — the singular call would
 * only ever return our own shadow host.
 */
export function elementAt(clientX: number, clientY: number): Element | null {
  const stack = document.elementsFromPoint(clientX, clientY)

  for (const el of stack) {
    if (el.id === WIDGET_HOST_ID) continue
    if (el === document.documentElement || el === document.body) continue
    return el
  }

  return null
}

/**
 * Resolves a set of sample points to the distinct elements beneath them.
 *
 * A pin samples once; a highlight or sketch samples several points across its
 * shape, so a box drawn around a price block reports the block and the prices
 * rather than only whatever sits dead centre.
 */
export function resolveTargets(clientPoints: Array<[number, number]>): NoteTarget[] {
  const seen = new Set<Element>()
  const targets: NoteTarget[] = []

  for (const [x, y] of clientPoints) {
    const el = elementAt(x, y)
    if (!el || seen.has(el)) continue

    seen.add(el)
    try {
      targets.push(describeElement(el, window.scrollX, window.scrollY))
    } catch {
      // A hostile or exotic element should never stop a note being saved.
    }

    if (targets.length >= TARGET_LIMITS.maxTargets) break
  }

  // Smallest box first. Sampling order is arbitrary — a stroke across a photo
  // may hit the wrapping <section> before the image — and consumers read the
  // first entry as "the element this note is about". The tightest box around
  // the annotation is the best answer to that, and the most useful to grep.
  return targets.sort((a, b) => area(a.rect) - area(b.rect))
}

function area(rect: { w: number; h: number }): number {
  return Math.max(0, rect.w) * Math.max(0, rect.h)
}

/** Evenly spaced sample points inside a rectangle, in viewport coordinates. */
export function sampleRect(x: number, y: number, w: number, h: number): Array<[number, number]> {
  const points: Array<[number, number]> = [[x + w / 2, y + h / 2]]

  for (const fx of [0.15, 0.5, 0.85]) {
    for (const fy of [0.15, 0.5, 0.85]) {
      points.push([x + w * fx, y + h * fy])
    }
  }

  return points
}

/** A handful of points along a stroke, including both ends. */
export function samplePolyline(points: Array<[number, number]>, wanted = 6): Array<[number, number]> {
  if (points.length <= wanted) return points

  const step = (points.length - 1) / (wanted - 1)
  const sampled: Array<[number, number]> = []
  for (let i = 0; i < wanted; i++) {
    const point = points[Math.round(i * step)]
    if (point) sampled.push(point)
  }
  return sampled
}
